/**
 * VAULT Inbox — Email-to-VAULT review queue.
 *
 * Surfaces the private forwarding address, the pipeline health, and the
 * expense-candidate review cards (Confirm / Edit / Ignore / Link / Rule).
 *
 * NOTHING on this page auto-creates records. Every action requires an
 * explicit user click.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Mail, Copy, RefreshCw, Check, X, Link2, ScrollText,
    ShieldCheck, AlertTriangle, ChevronRight, Sparkles,
    Pencil, Plus,
} from "lucide-react";

const CONF_COLOR = { HIGH: "text-emerald-700 bg-emerald-50 border-emerald-200",
                     MEDIUM: "text-amber-700 bg-amber-50 border-amber-200",
                     LOW: "text-stone-600 bg-stone-50 border-stone-200" };

export default function VaultInbox() {
    const [addr, setAddr] = useState(null);
    const [health, setHealth] = useState(null);
    const [candidates, setCandidates] = useState([]);
    const [summary, setSummary] = useState({});
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("NEEDS_REVIEW");

    const loadAll = async () => {
        setLoading(true);
        try {
            const [a, h, c, r] = await Promise.all([
                api.get("/inbox/address"),
                api.get("/inbox/health"),
                api.get("/inbox/expense-candidates", { params: { state: filter } }),
                api.get("/inbox/rules"),
            ]);
            setAddr(a.data);
            setHealth(h.data);
            setCandidates(c.data.rows || []);
            setSummary(c.data.summary || {});
            setRules(r.data.rows || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Unable to load VAULT Inbox.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, [filter]);

    const copyAddress = () => {
        if (!addr?.address) return;
        navigator.clipboard.writeText(addr.address);
        toast.success("Forwarding address copied.");
    };

    const regenerate = async () => {
        if (!window.confirm("Rotate your VAULT Inbox address? The current address will keep working for 24 hours."))
            return;
        try {
            const r = await api.post("/inbox/address/regenerate");
            setAddr(r.data);
            toast.success("New forwarding address issued.");
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not rotate address."); }
    };

    const confirmCandidate = async (c, edits = null) => {
        try {
            await api.post(`/inbox/expense-candidates/${c.id}/confirm`, {
                merchant: edits?.merchant ?? c.merchant,
                amount:   edits?.amount   ?? c.amount,
                currency: edits?.currency ?? c.currency,
                date:     edits?.date     ?? c.date,
                category: edits?.category ?? c.suggested_category,
            });
            toast.success(`Expense created for ${edits?.merchant ?? c.merchant ?? "unknown merchant"}.`);
            loadAll();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not confirm."); }
    };

    const ignoreCandidate = async (c) => {
        try { await api.post(`/inbox/expense-candidates/${c.id}/ignore`); loadAll(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Could not ignore."); }
    };

    const linkCandidate = async (c, expenseId) => {
        try {
            await api.post(`/inbox/expense-candidates/${c.id}/link`, { expense_id: expenseId });
            toast.success("Linked to existing expense.");
            loadAll();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not link."); }
    };

    const createRuleFromCandidate = async (c) => {
        try {
            const r = await api.post(`/inbox/expense-candidates/${c.id}/create-rule`);
            toast.success(r.data?.note || "Rule created (auto-confirm off).");
            loadAll();
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not create rule."); }
    };

    if (loading && !addr) {
        return <div className="p-8 text-stone-500" data-testid="vault-inbox-loading">Loading VAULT Inbox…</div>;
    }

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-8" data-testid="vault-inbox-page">
            {/* ── Header ─────────────────────────────────────────── */}
            <header>
                <div className="text-[11px] uppercase tracking-[0.24em] text-stone-500 font-semibold">Organise</div>
                <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mt-1">VAULT Inbox</h1>
                <p className="text-stone-600 mt-2 max-w-xl">
                    Forward it. Connect it. VAULT organises it. Receipts, statements and important
                    financial documents land here for review — nothing is added to your VAULT until you confirm.
                </p>
            </header>

            {/* ── Forwarding address ─────────────────────────────── */}
            <section className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="inbox-address-card">
                <div className="flex items-start gap-3">
                    <Mail size={18} className="text-amber-700 mt-1" />
                    <div className="flex-1">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold">
                            Your private forwarding address
                        </div>
                        <div className="mt-2 flex items-center gap-3 flex-wrap">
                            <code className="text-sm md:text-base font-mono bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-900" data-testid="inbox-address">
                                {addr?.address || "—"}
                            </code>
                            <button onClick={copyAddress} className="inline-flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-900 border border-stone-200 rounded-lg px-3 py-2" data-testid="inbox-copy-btn">
                                <Copy size={13} /> Copy
                            </button>
                            <button onClick={regenerate} className="inline-flex items-center gap-1.5 text-sm text-stone-700 hover:text-stone-900 border border-stone-200 rounded-lg px-3 py-2" data-testid="inbox-regenerate-btn">
                                <RefreshCw size={13} /> Regenerate
                            </button>
                        </div>
                        {addr?.previous_expires_at && (
                            <div className="mt-2 text-[12px] text-amber-700 flex items-center gap-1.5">
                                <AlertTriangle size={12} /> Old address stops accepting mail on {new Date(addr.previous_expires_at).toLocaleString()}.
                            </div>
                        )}
                        <p className="mt-3 text-[13px] text-stone-500 max-w-lg">
                            Forward a receipt, invoice, statement or financial document. VAULT will analyse it
                            and place it in your review queue below.
                        </p>
                    </div>
                </div>
            </section>

            {/* ── Connections health ─────────────────────────────── */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <ConnectionCard title="Email forwarding"    state={health?.postmark_configured ? "READY" : "AWAITING_SETUP"} note={health?.postmark_configured ? "Live receiver connected." : "Ask an admin to configure the Postmark inbound receiver."} testid="conn-postmark" />
                <ConnectionCard title="Microsoft Outlook"   state={health?.outlook_configured ? "READY" : "AWAITING_SETUP"} note={health?.outlook_configured ? "OAuth configured — connect from Settings → Connections." : "Coming soon. Awaiting Azure AD app registration."} testid="conn-outlook" />
                <ConnectionCard title="Gmail"                state="BETA_WAITLIST" note="Coming soon — pending Google OAuth verification." testid="conn-gmail" />
            </section>

            {/* ── Rules quick summary ────────────────────────────── */}
            <section className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="inbox-rules-card">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Trusted rules</div>
                        <h2 className="text-lg font-semibold text-stone-900 mt-1">
                            {rules.length} active {rules.length === 1 ? "rule" : "rules"}
                        </h2>
                    </div>
                    <Link to="/organise/vault-inbox/rules" className="inline-flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-800 font-semibold" data-testid="inbox-manage-rules">
                        Manage rules <ChevronRight size={14} />
                    </Link>
                </div>
                {rules.length === 0 && (
                    <p className="mt-3 text-sm text-stone-500 max-w-lg">
                        Rules classify incoming email deterministically. Auto-confirm is off by default — you enable it per rule after you trust the sender.
                    </p>
                )}
                {rules.length > 0 && (
                    <ul className="mt-3 space-y-2">
                        {rules.slice(0, 4).map((r) => (
                            <li key={r.id} className="text-sm text-stone-700 flex items-center gap-2">
                                <ScrollText size={13} className="text-stone-400" />
                                <span className="font-semibold">{r.name}</span>
                                {r.classification && <span className="text-stone-500">→ {r.classification.replace(/_/g, " ").toLowerCase()}</span>}
                                {r.auto_confirm && (
                                    <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Auto-confirm ON</span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* ── Expense candidates ─────────────────────────────── */}
            <section data-testid="inbox-candidates-section">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Review inbox</div>
                        <h2 className="text-xl font-semibold text-stone-900 mt-1">Possible expenses</h2>
                    </div>
                    <div className="inline-flex rounded-lg border border-stone-200 overflow-hidden">
                        {["NEEDS_REVIEW", "CONFIRMED", "IGNORED", "LINKED"].map((s) => (
                            <button
                                key={s}
                                onClick={() => setFilter(s)}
                                className={`text-[12px] px-3 py-1.5 ${filter === s ? "bg-stone-900 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}
                                data-testid={`inbox-filter-${s.toLowerCase()}`}
                            >
                                {s.replace("_", " ").toLowerCase()} ({summary[s] || 0})
                            </button>
                        ))}
                    </div>
                </div>

                {candidates.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-10 text-center" data-testid="inbox-empty">
                        <Sparkles size={22} className="text-stone-400 mx-auto" />
                        <p className="text-stone-600 mt-2">Nothing to review yet.</p>
                        <p className="text-[13px] text-stone-500 mt-1">
                            Forward a receipt to <span className="font-mono">{addr?.address}</span> to see it here.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {candidates.map((c) => (
                            <CandidateCard
                                key={c.id}
                                c={c}
                                onConfirm={(edits) => confirmCandidate(c, edits)}
                                onIgnore={() => ignoreCandidate(c)}
                                onLink={(expenseId) => linkCandidate(c, expenseId)}
                                onCreateRule={() => createRuleFromCandidate(c)}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* ── Privacy reassurance ────────────────────────────── */}
            <footer className="rounded-2xl border border-stone-200 bg-stone-50 p-5 text-[13px] text-stone-600 flex items-start gap-3" data-testid="inbox-privacy">
                <ShieldCheck size={16} className="text-stone-500 mt-0.5" />
                <div>
                    VAULT never stores or reads CVV, PIN, OTP or full card numbers from your email.
                    Forwarded messages are used only to identify financial information you asked us to organise.
                    Disconnect anytime from Settings → Connections.
                </div>
            </footer>
        </div>
    );
}

function ConnectionCard({ title, state, note, testid }) {
    const stateColor = state === "READY"           ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                     : state === "AWAITING_SETUP"  ? "text-amber-700 bg-amber-50 border-amber-200"
                     :                                "text-stone-600 bg-stone-50 border-stone-200";
    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={testid}>
            <div className="font-semibold text-stone-900">{title}</div>
            <span className={`mt-2 inline-block text-[11px] px-2 py-0.5 rounded-full border ${stateColor}`}>{state.replace("_", " ")}</span>
            <p className="mt-2 text-[13px] text-stone-600">{note}</p>
        </div>
    );
}

function CandidateCard({ c, onConfirm, onIgnore, onLink, onCreateRule }) {
    const band = c.confidence_band || "LOW";
    const [editing, setEditing]     = useState(false);
    const [linkOpen, setLinkOpen]   = useState(false);
    const [suggests, setSuggests]   = useState([]);
    const [form, setForm] = useState({
        merchant: c.merchant || "",
        amount:   c.amount ?? "",
        currency: c.currency || "INR",
        date:     c.date || "",
        category: c.suggested_category || "",
    });

    const openLinkPicker = async () => {
        setLinkOpen(true);
        // If there's a direct duplicate match, use that; otherwise fetch suggestions.
        if (c.duplicate_of_expense_id) {
            setSuggests([{ id: c.duplicate_of_expense_id, merchant: c.merchant,
                           amount: c.amount, date: c.date, __direct: true }]);
            return;
        }
        try {
            const r = await api.get(`/inbox/expense-candidates/${c.id}/link-suggestions`);
            setSuggests(r.data.rows || []);
        } catch { setSuggests([]); }
    };

    return (
        <article className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`inbox-candidate-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Review expense</div>
                    <h3 className="text-lg font-semibold text-stone-900 mt-1 truncate">
                        {c.merchant || "Unknown merchant"}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        {c.amount != null && (
                            <span className="text-stone-900 font-semibold">
                                {c.currency || "INR"} {Number(c.amount).toLocaleString()}
                            </span>
                        )}
                        {c.date && <span className="text-stone-600">{c.date}</span>}
                        {c.last4 && <span className="text-stone-600">Card •••• {c.last4}</span>}
                        {c.reference && <span className="text-stone-500">Ref {c.reference}</span>}
                        {c.suggested_category && (
                            <span className="text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[11px]">
                                {c.suggested_category}
                            </span>
                        )}
                    </div>
                    {c.source_from && (
                        <p className="mt-1 text-[12px] text-stone-500 truncate max-w-lg">
                            From: <span className="font-mono">{c.source_from}</span>
                        </p>
                    )}
                    {c.subject && (
                        <p className="mt-1 text-[13px] text-stone-500 truncate max-w-lg">{c.subject}</p>
                    )}
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full border shrink-0 ${CONF_COLOR[band]}`}>
                    Confidence {band}
                </span>
            </div>

            {c.duplicate_of_expense_id && !linkOpen && (
                <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-[13px] text-amber-800 flex items-center gap-2" data-testid={`dup-warning-${c.id}`}>
                    <AlertTriangle size={14} />
                    Possible match found.{" "}
                    <button onClick={openLinkPicker} className="underline font-semibold" data-testid={`dup-link-${c.id}`}>
                        Link instead?
                    </button>
                </div>
            )}

            {/* ── Edit form (spec: EDIT before CONFIRM) ─────────────── */}
            {editing && c.state === "NEEDS_REVIEW" && (
                <div className="mt-3 grid grid-cols-2 gap-2 border-t border-stone-100 pt-3" data-testid={`edit-form-${c.id}`}>
                    <label className="text-[11px] text-stone-500 col-span-2 md:col-span-1">
                        Merchant
                        <input value={form.merchant} onChange={(e) => setForm({...form, merchant: e.target.value})} className="mt-1 w-full text-sm border border-stone-200 rounded-md px-2 py-1.5" data-testid={`edit-merchant-${c.id}`} />
                    </label>
                    <label className="text-[11px] text-stone-500 col-span-2 md:col-span-1">
                        Amount
                        <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({...form, amount: parseFloat(e.target.value)})} className="mt-1 w-full text-sm border border-stone-200 rounded-md px-2 py-1.5" data-testid={`edit-amount-${c.id}`} />
                    </label>
                    <label className="text-[11px] text-stone-500">
                        Date
                        <input type="date" value={form.date || ""} onChange={(e) => setForm({...form, date: e.target.value})} className="mt-1 w-full text-sm border border-stone-200 rounded-md px-2 py-1.5" data-testid={`edit-date-${c.id}`} />
                    </label>
                    <label className="text-[11px] text-stone-500">
                        Category
                        <input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="mt-1 w-full text-sm border border-stone-200 rounded-md px-2 py-1.5" data-testid={`edit-category-${c.id}`} />
                    </label>
                </div>
            )}

            {/* ── Link picker ───────────────────────────────────────── */}
            {linkOpen && c.state === "NEEDS_REVIEW" && (
                <div className="mt-3 border-t border-stone-100 pt-3" data-testid={`link-picker-${c.id}`}>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-semibold mb-2">Link to an existing expense</div>
                    {suggests.length === 0 ? (
                        <p className="text-[13px] text-stone-500">No plausible matches found.</p>
                    ) : (
                        <ul className="space-y-1">
                            {suggests.map((s) => (
                                <li key={s.id} className="flex items-center justify-between text-sm border border-stone-200 rounded-lg px-3 py-2">
                                    <div className="min-w-0">
                                        <div className="truncate font-semibold text-stone-800">{s.merchant || s.id.slice(0, 8)}</div>
                                        <div className="text-[12px] text-stone-500">{s.date} · {s.currency || "INR"} {s.amount ? Number(s.amount).toLocaleString() : "—"}</div>
                                    </div>
                                    <button onClick={() => onLink(s.id)} className="text-xs bg-stone-900 text-white rounded-md px-2.5 py-1 hover:bg-stone-800" data-testid={`link-pick-${s.id}`}>
                                        Link
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button onClick={() => setLinkOpen(false)} className="mt-2 text-[12px] text-stone-500 hover:text-stone-800">Cancel</button>
                </div>
            )}

            {c.state === "NEEDS_REVIEW" && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                        onClick={() => onConfirm(editing ? form : null)}
                        disabled={!!c.duplicate_of_expense_id && !editing}
                        className="inline-flex items-center gap-1.5 text-sm bg-stone-900 text-white rounded-lg px-3 py-2 hover:bg-stone-800 disabled:opacity-50"
                        data-testid={`confirm-${c.id}`}
                    >
                        <Check size={14} /> {editing ? "Save & Confirm" : "Confirm Expense"}
                    </button>
                    <button
                        onClick={() => setEditing((v) => !v)}
                        className="inline-flex items-center gap-1.5 text-sm text-stone-700 border border-stone-200 rounded-lg px-3 py-2 hover:bg-stone-50"
                        data-testid={`edit-${c.id}`}
                    >
                        <Pencil size={14} /> {editing ? "Cancel edit" : "Edit"}
                    </button>
                    <button
                        onClick={onIgnore}
                        className="inline-flex items-center gap-1.5 text-sm text-stone-700 border border-stone-200 rounded-lg px-3 py-2 hover:bg-stone-50"
                        data-testid={`ignore-${c.id}`}
                    >
                        <X size={14} /> Ignore
                    </button>
                    <button
                        onClick={openLinkPicker}
                        className="inline-flex items-center gap-1.5 text-sm text-stone-700 border border-stone-200 rounded-lg px-3 py-2 hover:bg-stone-50"
                        data-testid={`link-btn-${c.id}`}
                    >
                        <Link2 size={14} /> Link Existing
                    </button>
                    <button
                        onClick={onCreateRule}
                        className="inline-flex items-center gap-1.5 text-sm text-amber-800 border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 hover:bg-amber-100"
                        data-testid={`create-rule-${c.id}`}
                        title="Trust future emails from this sender"
                    >
                        <Plus size={14} /> Create Rule
                    </button>
                </div>
            )}

            {c.state !== "NEEDS_REVIEW" && (
                <div className="mt-3 text-[12px] text-stone-500">
                    Status: <span className="font-semibold text-stone-700">{c.state}</span>
                    {c.created_expense_id && <> · Expense <code>{c.created_expense_id.slice(0, 8)}…</code></>}
                    {c.linked_expense_id && <> · Linked to <code>{c.linked_expense_id.slice(0, 8)}…</code></>}
                </div>
            )}
        </article>
    );
}
